import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Report {
  id: number
  report_type: number
  category: number
  report_date: string | null
  day_name: string | null
  investigator_name: string | null
  user_name: string | null
  team_members: string[]
  status: number
  significant: number
  used_in_final: number
  entries_count: number
  can_update: boolean
  can_destroy: boolean
}

interface TeamMemberOption {
  id: number
  name: string
}

interface Props {
  case_record: CaseRecord
  reports: Report[]
  current_category: string
  current_status: string
  current_start_date: string
  current_end_date: string
  current_team_member_ids: number[]
  team_member_options: TeamMemberOption[]
  can_download_final_report: boolean
}

const STATUS_LABELS: Record<number, string> = {
  0: "Draft",
  1: "Submitted",
  2: "Approved",
  3: "Prepared",
}

const STATUS_STYLES: Record<number, string> = {
  0: "bg-hpi-pale-gray text-hpi-dark-gray",
  1: "bg-hpi-yellow/30 text-hpi-dark-gray",
  2: "bg-hpi-dark-green/20 text-hpi-dark-green",
  3: "bg-hpi-light-blue text-hpi-dark-blue",
}

const CATEGORY_LABELS: Record<number, string> = {
  0: "Daily",
  1: "GPS",
  3: "Incident",
}

const statusBadge = (status: number) => (
  <span
    className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLES[status] || "bg-hpi-pale-gray text-hpi-dark-gray"}`}
  >
    {STATUS_LABELS[status] || "Unknown"}
  </span>
)

export default function Index({
  case_record,
  reports,
  current_category,
  current_status,
  current_start_date,
  current_end_date,
  current_team_member_ids,
  team_member_options,
  can_download_final_report,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Reports", href: `/cases/${case_record.id}/reports` },
  ]

  const [startDate, setStartDate] = useState(current_start_date || "")
  const [endDate, setEndDate] = useState(current_end_date || "")
  const [selectedMembers, setSelectedMembers] = useState<number[]>(
    current_team_member_ids || [],
  )

  const basePath = `/cases/${case_record.id}/reports`

  const categoryFilters = [
    { key: "all", label: "All" },
    { key: "0", label: "Daily" },
    { key: "1", label: "GPS" },
    { key: "3", label: "Incident" },
  ]

  const statusFilters = [
    { key: "all", label: "All" },
    { key: "0", label: "Draft" },
    { key: "1", label: "Submitted" },
    { key: "2", label: "Approved" },
    { key: "3", label: "Prepared" },
  ]

  const buildFilterUrl = (
    category: string,
    status: string,
    start?: string,
    end?: string,
    members?: number[],
  ) => {
    const params = new URLSearchParams()
    if (category !== "all") params.set("category", category)
    if (status !== "all") params.set("status", status)
    const sd = start ?? startDate
    const ed = end ?? endDate
    if (sd) params.set("start_date", sd)
    if (ed) params.set("end_date", ed)
    const memberIds = members ?? selectedMembers
    memberIds.forEach((id) => params.append("team_member_ids[]", String(id)))
    const qs = params.toString()
    return qs ? `${basePath}?${qs}` : basePath
  }

  const toggleMember = (id: number) => {
    setSelectedMembers((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id],
    )
  }

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this report?")) {
      router.delete(`${basePath}/${id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Reports - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Reports for {case_record.display_name}
          </h1>
          <div className="flex items-center gap-2">
            {can_download_final_report && (
              <>
                <Link
                  href={`/cases/${case_record.id}/final_report_preview_by_report`}
                  className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-pale-blue rounded border px-4 py-2"
                >
                  Preview Final Report
                </Link>
                <a
                  href={`/cases/${case_record.id}/report_downloads/supporting_detail_word`}
                  className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-pale-blue rounded border px-4 py-2"
                >
                  Supporting Detail
                </a>
                <Link
                  href={`/cases/${case_record.id}/reports/select_final_reports`}
                  className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-pale-blue rounded border px-4 py-2"
                >
                  Build Final Report (Word)
                </Link>
              </>
            )}
            <Link
              href={`${basePath}/new`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Add Report
            </Link>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <span className="text-hpi-med-gray text-sm">Category:</span>
            {categoryFilters.map((f) => (
              <Link
                key={f.key}
                href={buildFilterUrl(f.key, current_status)}
                className={`rounded px-3 py-1 text-sm ${
                  current_category === f.key
                    ? "bg-hpi-dark-blue text-white"
                    : "bg-hpi-pale-gray text-hpi-dark-gray hover:bg-hpi-pale-gray"
                }`}
              >
                {f.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-hpi-med-gray text-sm">Status:</span>
            {statusFilters.map((f) => (
              <Link
                key={f.key}
                href={buildFilterUrl(current_category, f.key)}
                className={`rounded px-3 py-1 text-sm ${
                  current_status === f.key
                    ? "bg-hpi-dark-blue text-white"
                    : "bg-hpi-pale-gray text-hpi-dark-gray hover:bg-hpi-pale-gray"
                }`}
              >
                {f.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="border-hpi-light-gray bg-hpi-pale-gray mb-4 rounded border p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="text-sm font-medium">Date Range</div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="filter_start_date"
                className="text-hpi-med-gray text-sm"
              >
                Start
              </label>
              <input
                id="filter_start_date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="filter_end_date"
                className="text-hpi-med-gray text-sm"
              >
                End
              </label>
              <input
                id="filter_end_date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
              />
            </div>
          </div>
          {team_member_options.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <div className="text-sm font-medium">Team Members</div>
              {team_member_options.map((member) => (
                <label
                  key={member.id}
                  className="flex items-center gap-1 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(member.id)}
                    onChange={() => toggleMember(member.id)}
                    className="accent-hpi-dark-blue"
                  />
                  {member.name}
                </label>
              ))}
            </div>
          )}
          <div className="mt-3 flex items-center gap-3">
            <Link
              href={buildFilterUrl(
                current_category,
                current_status,
                undefined,
                undefined,
                selectedMembers,
              )}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-1 text-sm text-white"
            >
              Search
            </Link>
            {(startDate || endDate || selectedMembers.length > 0) && (
              <Link
                href={buildFilterUrl(
                  current_category,
                  current_status,
                  "",
                  "",
                  [],
                )}
                className="text-hpi-med-gray hover:text-hpi-dark-gray text-sm underline"
                onClick={() => {
                  setStartDate("")
                  setEndDate("")
                  setSelectedMembers([])
                }}
              >
                Clear all filters
              </Link>
            )}
          </div>
        </div>

        {reports.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border text-sm">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Type
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Team
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Status
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2 whitespace-nowrap">
                    <Link
                      href={`${basePath}/${report.id}`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue"
                    >
                      {formatDate(report.report_date)}
                      {report.day_name && (
                        <span className="text-hpi-med-gray ml-1 text-xs">
                          {report.day_name}
                        </span>
                      )}
                    </Link>
                    {report.significant === 1 && (
                      <span className="ml-2 rounded bg-purple-100 px-2 py-0.5 text-xs text-purple-700">
                        Significant
                      </span>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {CATEGORY_LABELS[report.category] || "Unknown"}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {(() => {
                      const leader =
                        report.investigator_name ?? report.user_name
                      const others = report.team_members.filter(
                        (name) => name !== report.investigator_name,
                      )
                      return (
                        <>
                          <span className="font-bold">{leader}</span>
                          {others.length > 0 && (
                            <span className="text-hpi-med-gray">
                              {" / "}
                              {others.join(" / ")}
                            </span>
                          )}
                        </>
                      )
                    })()}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {statusBadge(report.status)}
                    {report.used_in_final === 0 && report.status >= 2 && (
                      <span className="text-hpi-red ml-2 text-xs">
                        Not used in Final Report
                      </span>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 whitespace-nowrap">
                    <Link
                      href={`${basePath}/${report.id}`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                    >
                      View
                    </Link>
                    {report.can_update && (
                      <Link
                        href={`${basePath}/${report.id}/edit`}
                        className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                      >
                        Edit
                      </Link>
                    )}
                    {report.can_destroy && (
                      <button
                        onClick={() => handleDelete(report.id)}
                        className="text-hpi-red hover:text-hpi-red"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No reports found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Case
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
